import { useCallback, useEffect, useMemo } from 'react';
import { isTextarea, isCustomUrlProperty } from 'customer-data-objects/property/PropertyIdentifier';
import useComponentMountMetric from '../metrics/useComponentMountMetric';
import { getTransferrableProps } from '../utils/getTransferrableProps';
import { useProperty } from '../v2/hooks/useProperty';
import { usePropertyValue } from '../v2/hooks/usePropertyValue';
import { useGetIsUngated } from '../v2/hooks/useGetIsUngated';
import { ExpandableText } from 'foundations-components/transitional/components/ExpandableText';
import { ExpandingTextArea } from 'foundations-components/transitional/components/ExpandingTextArea';
import { Tooltip } from 'foundations-components/transitional/components/Tooltip';
import { usePropertyValueFormatting } from '../v2/hooks/usePropertyValueFormatting';
import { trackExpandableTextCollapse, trackExpandableTextExpand } from '../utils/usageTrackingUtils';
import { useObjectTypeId } from '../v2/hooks/useObjectTypeId';
import { usePropertyValidationRules } from '../v2/hooks/usePropertyValidationRules';
import { useIsFocused } from '../v2/hooks/useIsFocused';
import { useHasUrlValidationRule } from '../v2/hooks/useHasUrlValidationRule';
import { List, ListItem } from 'foundations-components/transitional/components/List';
import FormattedMessage from 'I18n/components/FormattedMessage';
import { jsx as _jsx } from "react/jsx-runtime";
import { jsxs as _jsxs } from "react/jsx-runtime";
const DEFAULT_PROTOCOL = 'https://';
const PropertyInputExpandableText = props => {
  const isFocused = useIsFocused();
  const {
    onChange,
    onFocus,
    onBlur,
    inputRef,
    defaultValue
  } = props;
  const property = useProperty();
  const objectTypeId = useObjectTypeId();
  const value = usePropertyValue();
  const {
    onChange: normalizeInputOnChange,
    normalizationRules
  } = usePropertyValueFormatting({
    onChange
  });
  useComponentMountMetric('PropertyInputExpandableText');
  const validationRules = usePropertyValidationRules();
  const hasUrlValidationRule = useHasUrlValidationRule(validationRules.data);
  const isUrlProperty = isCustomUrlProperty(property) || hasUrlValidationRule;
  const handleChange = useCallback(event => {
    const newValue = event.target.value;

    // Handle URL protocol duplication when pasting
    if (isUrlProperty) {
      const currentValue = value || '';

      // Pasting a full URL when there's already content starting with https://
      if (currentValue.startsWith('https://') && newValue.match(/^https:\/\/https?:\/\//)) {
        const cleanedValue = newValue.replace(/^https:\/\//, '');
        return normalizeInputOnChange(Object.assign({}, event, {
          target: Object.assign({}, event.target, {
            value: cleanedValue
          })
        }));
      }
    }
    if (!isTextarea(property)) {
      if (event.target.value.includes('\n')) {
        const withoutNewlines = newValue.replace(/\n/g, '');
        if (withoutNewlines === value) {
          return; // no change, don't call handler
        }

        // prevent newlines if not a textarea, for why we're doing this see https://git.hubteam.com/HubSpot/CRM-Issues/issues/4221
        return normalizeInputOnChange(Object.assign({}, event, {
          target: Object.assign({}, event.target, {
            value: withoutNewlines
          })
        }));
      }
    }
    return normalizeInputOnChange(Object.assign({}, event, {
      target: Object.assign({}, event.target, {
        value: newValue
      })
    }));
  }, [normalizeInputOnChange, property, value, isUrlProperty]);
  const handleFocus = useCallback(event => {
    if (isUrlProperty && !value) {
      const newCursorPosition = DEFAULT_PROTOCOL.length;
      normalizeInputOnChange(Object.assign({}, event, {
        target: Object.assign({}, event.target, {
          value: DEFAULT_PROTOCOL
        })
      }));
      requestAnimationFrame(() => {
        if (inputRef !== null && inputRef !== void 0 && inputRef.current) {
          inputRef.current.setSelectionRange(newCursorPosition, newCursorPosition);
        }
      });
    }
    onFocus === null || onFocus === void 0 || onFocus(event);
  }, [isUrlProperty, normalizeInputOnChange, onFocus, value, inputRef]);
  useEffect(() => {
    const shouldInsertUrlProtocol = hasUrlValidationRule && isFocused && !value;
    if (!shouldInsertUrlProtocol) {
      return;
    }
    const event = {
      target: {
        value: DEFAULT_PROTOCOL
      }
    };
    normalizeInputOnChange(event);
    // This is a special case of adding the url protocol for non-custom URL properties.
    // The validation rules load async, so the protocol won't be added in the handleFocus callback.
    // We want this effect to run only when shouldInsertUrlProtocol updates and the rules
    // are loaded before any input to insert the protocol. For any subsequent focus events,
    // this behavior is handled in handleFocus.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [hasUrlValidationRule]);
  const handleBlur = useCallback(event => {
    if (isUrlProperty && value === DEFAULT_PROTOCOL) {
      normalizeInputOnChange(Object.assign({}, event, {
        target: Object.assign({}, event.target, {
          value: ''
        })
      }));
    }
    onBlur === null || onBlur === void 0 || onBlur(event);
  }, [isUrlProperty, normalizeInputOnChange, onBlur, value]);
  const getIsUngated = useGetIsUngated();
  const isUngatedForNormalization = getIsUngated('CRM:PropMgmt:PropertyCasingAndSpacingNormalization');
  const hasHandledNormalizationRules = useMemo(() => {
    if (normalizationRules.length === 0) {
      return false;
    }
    if (normalizationRules.includes('ALPHANUMERIC') || normalizationRules.includes('SPECIAL_CHARACTERS') || normalizationRules.includes('WHITESPACE') && isUngatedForNormalization) {
      return true;
    }
  }, [normalizationRules, isUngatedForNormalization]);
  return /*#__PURE__*/_jsx(Tooltip, {
    disabled: !hasHandledNormalizationRules,
    headingText: /*#__PURE__*/_jsx(FormattedMessage, {
      message: "customerDataProperties.NormalizationMessage.heading"
    }),
    title: /*#__PURE__*/_jsxs(List, {
      styled: true,
      children: [normalizationRules.includes('ALPHANUMERIC') && /*#__PURE__*/_jsx(ListItem, {
        children: /*#__PURE__*/_jsx(FormattedMessage, {
          message: "customerDataProperties.NormalizationMessage.numbersOnly"
        })
      }), normalizationRules.includes('SPECIAL_CHARACTERS') && /*#__PURE__*/_jsx(ListItem, {
        children: /*#__PURE__*/_jsx(FormattedMessage, {
          message: "customerDataProperties.NormalizationMessage.dontAllowSpecialCharacters"
        })
      }), isUngatedForNormalization && normalizationRules.includes('WHITESPACE') && /*#__PURE__*/_jsx(ListItem, {
        children: /*#__PURE__*/_jsx(FormattedMessage, {
          message: "customerDataProperties.NormalizationMessage.noWhitespace"
        })
      })]
    }),
    open: Boolean(hasHandledNormalizationRules),
    use: "longform",
    children: /*#__PURE__*/_jsx(ExpandableText, {
      buttonAlign: "left",
      hideButtonCaret: true,
      onCollapseButtonClick: () => trackExpandableTextCollapse({
        component: 'PropertyInputExpandableText',
        propertyName: property.name,
        objectTypeId
      }),
      onExpandButtonClick: () => trackExpandableTextExpand({
        component: 'PropertyInputExpandableText',
        propertyName: property.name,
        objectTypeId
      })
      // @ts-expect-error - borderRadius works here and fixes a visual bug, but is not yet added to the type
      ,
      borderRadius: 100,
      children: /*#__PURE__*/_jsx(ExpandingTextArea, Object.assign({}, getTransferrableProps(props), {
        value: value === null ? defaultValue || '' : value,
        inputRef: inputRef,
        onChange: handleChange,
        onFocus: handleFocus,
        onBlur: handleBlur,
        shrink: true
      }))
    })
  });
};
export default PropertyInputExpandableText;